// components/CustomerCapabilityAssessment/CustomerCapabilityAssessment.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../lib/apiClient';
import './CustomerCapabilityAssessment.css';

function CustomerCapabilityAssessment({ customerId, customerName, onClose }) {
  const [capabilities, setCapabilities] = useState([]);
  const [assessments, setAssessments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterHasCapability, setFilterHasCapability] = useState('all');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [metrics, setMetrics] = useState({
    total: 0,
    hasCapability: 0,
    purchased: 0,
    deployed: 0,
    inProgress: 0
  });
  
  // Canvas component linking state
  const [canvasComponents, setCanvasComponents] = useState([]);
  const [showComponentLink, setShowComponentLink] = useState(false);
  const [selectedCapabilityForLink, setSelectedCapabilityForLink] = useState(null);
  
  const saveTimeoutRef = useRef(null);

  // Deployment status options
  const deploymentStatuses = [
    { value: 'not_deployed', label: 'Not Deployed', color: '#ef4444' },
    { value: 'in_progress', label: 'In Progress', color: '#f59e0b' },
    { value: 'deployed', label: 'Deployed', color: '#10b981' }
  ];

  // Fetch capabilities and assessments on mount and when customer changes
  useEffect(() => {
    if (customerId) {
      // Reset state when customer changes
      setAssessments({});
      setSelectedRows(new Set());
      setExpandedNotes(new Set());
      setShowBulkActions(false);
      setCanvasComponents([]);
      fetchCapabilitiesAndAssessments();
      fetchCanvasComponents();
    }
  }, [customerId]);

  // Calculate metrics whenever assessments change
  useEffect(() => {
    calculateMetrics();
  }, [assessments, capabilities]);

  const fetchCapabilitiesAndAssessments = async () => {
    setLoading(true);
    try {
      // Fetch all capabilities
      const capData = await apiClient.get('/capabilities');
      setCapabilities(capData);

      // Extract unique categories
      const uniqueCategories = [...new Set(capData.map(cap => cap.category).filter(Boolean))];
      setCategories(uniqueCategories);

      // Fetch customer assessments
      const assessData = await apiClient.get(`/customers/${customerId}/capability-assessments`);
      // Convert array to object keyed by capability_id
      const assessmentMap = {};
      assessData.forEach(assessment => {
        assessmentMap[assessment.capability_id] = assessment;
      });
      setAssessments(assessmentMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch canvas components
  const fetchCanvasComponents = async () => {
    try {
      const data = await apiClient.get(`/customers/${customerId}/canvas`);
      if (data.canvas_data) {
        const canvasData = typeof data.canvas_data === 'string'
          ? JSON.parse(data.canvas_data)
          : data.canvas_data;
        setCanvasComponents(canvasData.elements || []);
      }
    } catch (error) {
      console.error('Error fetching canvas components:', error);
    }
  };

  const calculateMetrics = () => {
    const total = capabilities.length;
    let hasCapability = 0;
    let purchased = 0;
    let deployed = 0;
    let inProgress = 0;

    capabilities.forEach(cap => {
      const assessment = assessments[cap.capability_id];
      if (assessment?.has_capability) {
        hasCapability++;
        if (assessment.is_purchased) {
          purchased++;
        }
        if (assessment.deployment_status === 'deployed') {
          deployed++;
        } else if (assessment.deployment_status === 'in_progress') {
          inProgress++;
        }
      }
    });

    setMetrics({ total, hasCapability, purchased, deployed, inProgress });
  };

  // Link capability to canvas components
  const linkCapabilityToComponent = (capabilityId, componentId) => {
    // Save this relationship in the assessment notes or metadata
    const assessment = assessments[capabilityId] || {};
    const linkedComponents = assessment.linked_components || [];
    
    if (componentId) {
      // Add component
      if (!linkedComponents.includes(componentId)) {
        linkedComponents.push(componentId);
      }
    }
    
    // Update the assessment with linked components
    const updatedNotes = assessment.notes || '';
    const componentNames = canvasComponents
      .filter(c => linkedComponents.includes(c.id))
      .map(c => c.name)
      .join(', ');
    
    const linkNote = componentNames ? `\n[Linked to: ${componentNames}]` : '';
    const cleanNotes = updatedNotes.replace(/\n\[Linked to:.*?\]/g, '');
    
    handleFieldUpdate(capabilityId, 'notes', cleanNotes + linkNote);
    
    // Store in metadata (you might want to add a metadata field to your database)
    // For now, we'll store it in the notes field
  };

  // Auto-save functionality
  const saveAssessment = useCallback(async (capabilityId, field, value) => {
    const assessment = assessments[capabilityId] || {
      capability_id: capabilityId,
      has_capability: false,
      solution_used: '',
      is_purchased: false,
      deployment_status: 'not_deployed',
      responsible_party: '',
      notes: ''
    };

    const updatedAssessment = { ...assessment, [field]: value };

    // Update local state immediately
    setAssessments(prev => ({
      ...prev,
      [capabilityId]: updatedAssessment
    }));

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for saving
    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await apiClient.put(
          `/customers/${customerId}/capability-assessments/${capabilityId}`,
          updatedAssessment
        );
      } catch (error) {
        console.error('Error saving assessment:', error);
        // Optionally revert the change
      } finally {
        setSaving(false);
      }
    }, 500); // 500ms debounce
  }, [assessments, customerId]);

  // Handle field updates
  const handleFieldUpdate = (capabilityId, field, value) => {
    // Special handling for has_capability - if unchecked, reset related fields
    if (field === 'has_capability' && !value) {
      setAssessments(prev => ({
        ...prev,
        [capabilityId]: {
          ...prev[capabilityId],
          has_capability: false,
          solution_used: '',
          is_purchased: false,
          deployment_status: 'not_deployed',
          responsible_party: ''
        }
      }));
      saveAssessment(capabilityId, 'has_capability', false);
    } else {
      saveAssessment(capabilityId, field, value);
    }
  };

  // Bulk operations
  const handleBulkUpdate = async (field, value) => {
    if (selectedRows.size === 0) return;

    setSaving(true);
    const updates = [];
    
    selectedRows.forEach(capabilityId => {
      const assessment = assessments[capabilityId] || {
        capability_id: capabilityId,
        has_capability: false,
        solution_used: '',
        is_purchased: false,
        deployment_status: 'not_deployed',
        responsible_party: '',
        notes: ''
      };

      // For bulk operations, ensure has_capability is true if updating other fields
      if (field !== 'has_capability' && !assessment.has_capability) {
        assessment.has_capability = true;
      }

      updates.push({ ...assessment, [field]: value });
    });

    try {
      await apiClient.post(
        `/customers/${customerId}/capability-assessments/bulk`,
        { assessments: updates }
      );

      // Update local state
      const newAssessments = { ...assessments };
      updates.forEach(update => {
        newAssessments[update.capability_id] = update;
      });
      setAssessments(newAssessments);
      setSelectedRows(new Set());
      setShowBulkActions(false);
    } catch (error) {
      console.error('Error in bulk update:', error);
    } finally {
      setSaving(false);
    }
  };

  // Toggle row selection
  const toggleRowSelection = (capabilityId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(capabilityId)) {
        newSet.delete(capabilityId);
      } else {
        newSet.add(capabilityId);
      }
      return newSet;
    });
  };

  // Select all visible rows
  const selectAllVisible = () => {
    const visibleIds = getFilteredCapabilities().map(cap => cap.capability_id);
    setSelectedRows(new Set(visibleIds));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedRows(new Set());
  };

  // Filter capabilities
  const getFilteredCapabilities = () => {
    return capabilities.filter(cap => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          cap.name?.toLowerCase().includes(searchLower) ||
          cap.description?.toLowerCase().includes(searchLower) ||
          cap.cisco_product?.toLowerCase().includes(searchLower) ||
          assessments[cap.capability_id]?.solution_used?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Category filter
      if (filterCategory !== 'all' && cap.category !== filterCategory) {
        return false;
      }

      // Has capability filter
      const assessment = assessments[cap.capability_id];
      if (filterHasCapability === 'yes' && !assessment?.has_capability) {
        return false;
      }
      if (filterHasCapability === 'no' && assessment?.has_capability) {
        return false;
      }

      // Deployment status filter
      if (filterStatus !== 'all') {
        if (!assessment?.has_capability) return false;
        if (assessment.deployment_status !== filterStatus) return false;
      }

      return true;
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Capability Name',
      'Category',
      'Phase',
      'Has Capability',
      'Solution Used',
      'Purchased',
      'Deployment Status',
      'Responsible Party',
      'Canvas Components',
      'Notes'
    ];

    const rows = getFilteredCapabilities().map(cap => {
      const assessment = assessments[cap.capability_id] || {};
      const linkedMatch = assessment.notes?.match(/\[Linked to: (.*?)\]/);
      const linkedComponents = linkedMatch ? linkedMatch[1] : '';
      
      return [
        cap.name,
        cap.category || '',
        cap.cal_secure_phase || '',
        assessment.has_capability ? 'Yes' : 'No',
        assessment.solution_used || '',
        assessment.is_purchased ? 'Yes' : 'No',
        assessment.deployment_status || 'not_deployed',
        assessment.responsible_party || '',
        linkedComponents,
        assessment.notes?.replace(/\n\[Linked to:.*?\]/g, '') || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${customerName}_capability_assessment_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="assessment-loading">Loading capability assessment...</div>
      </div>
    );
  }

  const filteredCapabilities = getFilteredCapabilities();
  const linkedCapabilitiesCount = Object.values(assessments).filter(a => 
    a.notes && a.notes.includes('[Linked to:')
  ).length;

  return (
    <div className="assessment-container">
      <div className="assessment-header">
        <div className="assessment-title-section">
          <h2 className="assessment-title">Capability Assessment - {customerName}</h2>
          <button onClick={onClose} className="assessment-close-button">✕</button>
        </div>
        
        <div className="assessment-metrics">
          <div className="metric-card">
            <div className="metric-value">{metrics.hasCapability}</div>
            <div className="metric-label">Have Capability</div>
            <div className="metric-percent">{metrics.total > 0 ? Math.round((metrics.hasCapability / metrics.total) * 100) : 0}%</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{metrics.purchased}</div>
            <div className="metric-label">Purchased</div>
            <div className="metric-percent">{metrics.hasCapability > 0 ? Math.round((metrics.purchased / metrics.hasCapability) * 100) : 0}%</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{metrics.deployed}</div>
            <div className="metric-label">Deployed</div>
            <div className="metric-percent">{metrics.hasCapability > 0 ? Math.round((metrics.deployed / metrics.hasCapability) * 100) : 0}%</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{metrics.inProgress}</div>
            <div className="metric-label">In Progress</div>
            <div className="metric-percent">{metrics.hasCapability > 0 ? Math.round((metrics.inProgress / metrics.hasCapability) * 100) : 0}%</div>
          </div>
        </div>
      </div>

      {canvasComponents.length > 0 && (
        <div className="deployment-coverage-summary">
          <h3>Deployment Coverage</h3>
          <div className="coverage-stats">
            <div className="coverage-stat">
              <span className="stat-label">Canvas Components:</span>
              <span className="stat-value">{canvasComponents.length}</span>
            </div>
            <div className="coverage-stat">
              <span className="stat-label">Deployed Capabilities:</span>
              <span className="stat-value">{metrics.deployed}</span>
            </div>
            <div className="coverage-stat">
              <span className="stat-label">Linked Capabilities:</span>
              <span className="stat-value">{linkedCapabilitiesCount}</span>
            </div>
          </div>
        </div>
      )}

      <div className="assessment-controls">
        <div className="assessment-filters">
          <input
            type="text"
            placeholder="Search capabilities or solutions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="assessment-search"
          />
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="assessment-filter"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          
          <select
            value={filterHasCapability}
            onChange={(e) => setFilterHasCapability(e.target.value)}
            className="assessment-filter"
          >
            <option value="all">All Capabilities</option>
            <option value="yes">Has Capability</option>
            <option value="no">Missing Capability</option>
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="assessment-filter"
          >
            <option value="all">All Statuses</option>
            {deploymentStatuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
        
        <div className="assessment-actions">
          {selectedRows.size > 0 && (
            <span className="selected-count">{selectedRows.size} selected</span>
          )}
          <button
            onClick={() => setShowBulkActions(!showBulkActions)}
            className="assessment-button"
            disabled={selectedRows.size === 0}
          >
            🔧 Bulk Actions
          </button>
          <button onClick={exportToCSV} className="assessment-button">
            📥 Export CSV
          </button>
        </div>
      </div>

      {showBulkActions && selectedRows.size > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-actions-label">Bulk update {selectedRows.size} items:</span>
          <button 
            onClick={() => handleBulkUpdate('has_capability', true)}
            className="bulk-action-button"
          >
            ✓ Has Capability
          </button>
          <button 
            onClick={() => handleBulkUpdate('is_purchased', true)}
            className="bulk-action-button"
          >
            💰 Mark Purchased
          </button>
          <select
            onChange={(e) => e.target.value && handleBulkUpdate('deployment_status', e.target.value)}
            className="bulk-action-select"
            defaultValue=""
          >
            <option value="">Set Status...</option>
            {deploymentStatuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <button onClick={clearSelection} className="bulk-action-button secondary">
            Clear Selection
          </button>
        </div>
      )}

      <div className="assessment-table-container">
        <table className="assessment-table">
          <thead>
            <tr>
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                  checked={selectedRows.size > 0 && selectedRows.size === filteredCapabilities.length}
                />
              </th>
              <th>Capability</th>
              <th className="center-column">Has?</th>
              <th>Solution Used</th>
              <th className="center-column">Purchased?</th>
              <th>Deployment Status</th>
              <th>Canvas Components</th>
              <th>Responsible Party</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredCapabilities.map(capability => {
              const assessment = assessments[capability.capability_id] || {};
              const isExpanded = expandedNotes.has(capability.capability_id);
              
              return (
                <tr key={capability.capability_id} className={assessment.has_capability ? 'has-capability' : ''}>
                  <td className="checkbox-column" data-label="Select">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(capability.capability_id)}
                      onChange={() => toggleRowSelection(capability.capability_id)}
                    />
                  </td>

                  <td className="capability-cell" data-label="Capability">
                    <div className="capability-info">
                      <strong>{capability.name}</strong>
                      {capability.category && (
                        <span className="capability-category">{capability.category}</span>
                      )}
                      {capability.cal_secure_phase && (
                        <span className={`phase-badge phase-${capability.cal_secure_phase}`}>
                          Phase {capability.cal_secure_phase}
                        </span>
                      )}
                      {capability.description && (
                        <div className="capability-description">{capability.description}</div>
                      )}
                    </div>
                  </td>
                  
                  <td className="center-column" data-label="Has?">
                    <input
                      type="checkbox"
                      checked={assessment.has_capability || false}
                      onChange={(e) => handleFieldUpdate(capability.capability_id, 'has_capability', e.target.checked)}
                      className="capability-checkbox"
                    />
                  </td>

                  <td data-label="Solution Used">
                    <input
                      type="text"
                      value={assessment.solution_used || ''}
                      onChange={(e) => handleFieldUpdate(capability.capability_id, 'solution_used', e.target.value)}
                      placeholder="Enter solution..."
                      className="assessment-input"
                      disabled={!assessment.has_capability}
                    />
                  </td>

                  <td className="center-column" data-label="Purchased?">
                    <input
                      type="checkbox"
                      checked={assessment.is_purchased || false}
                      onChange={(e) => handleFieldUpdate(capability.capability_id, 'is_purchased', e.target.checked)}
                      disabled={!assessment.has_capability}
                      className="capability-checkbox"
                    />
                  </td>

                  <td data-label="Deployment Status">
                    <select
                      value={assessment.deployment_status || 'not_deployed'}
                      onChange={(e) => handleFieldUpdate(capability.capability_id, 'deployment_status', e.target.value)}
                      className="assessment-select"
                      disabled={!assessment.has_capability}
                      style={{
                        backgroundColor: deploymentStatuses.find(s => s.value === assessment.deployment_status)?.color + '20',
                        borderColor: deploymentStatuses.find(s => s.value === assessment.deployment_status)?.color
                      }}
                    >
                      {deploymentStatuses.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  
                  <td data-label="Canvas Components">
                    {assessment.has_capability && assessment.deployment_status === 'deployed' && (
                      <div className="component-link-cell">
                        <button
                          onClick={() => {
                            setSelectedCapabilityForLink(capability.capability_id);
                            setShowComponentLink(true);
                          }}
                          className="link-component-button"
                          title="Link to canvas components"
                        >
                          🔗 Link
                        </button>
                        {assessment.notes && assessment.notes.includes('[Linked to:') && (
                          <div className="linked-components-badge">
                            {assessment.notes.match(/\[Linked to: (.*?)\]/)?.[1] || ''}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  
                  <td data-label="Responsible Party">
                    <input
                      type="text"
                      value={assessment.responsible_party || ''}
                      onChange={(e) => handleFieldUpdate(capability.capability_id, 'responsible_party', e.target.value)}
                      placeholder="Enter name..."
                      className="assessment-input"
                      disabled={!assessment.has_capability}
                    />
                  </td>

                  <td className="notes-cell" data-label="Notes">
                    <div className="notes-container">
                      <textarea
                        value={assessment.notes || ''}
                        onChange={(e) => handleFieldUpdate(capability.capability_id, 'notes', e.target.value)}
                        placeholder="Add notes..."
                        className={`assessment-notes ${isExpanded ? 'expanded' : ''}`}
                        rows={isExpanded ? 4 : 1}
                      />
                      {assessment.notes && (
                        <button
                          onClick={() => setExpandedNotes(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(capability.capability_id)) {
                              newSet.delete(capability.capability_id);
                            } else {
                              newSet.add(capability.capability_id);
                            }
                            return newSet;
                          })}
                          className="expand-notes-button"
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredCapabilities.length === 0 && (
          <div className="assessment-empty-state">
            No capabilities found matching your filters.
          </div>
        )}
      </div>
      
      {saving && (
        <div className="saving-indicator">
          <span className="saving-spinner"></span>
          Saving...
        </div>
      )}
      
      {showComponentLink && (
        <div className="modal-overlay" onClick={() => setShowComponentLink(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Link Capability to Canvas Components</h3>
            <p className="modal-description">
              Select which components on the canvas implement this capability
            </p>
            
            <div className="component-list">
              {canvasComponents.length > 0 ? (
                canvasComponents.map(component => (
                  <div key={component.id} className="component-item">
                    <label className="component-label">
                      <input
                        type="checkbox"
                        checked={
                          assessments[selectedCapabilityForLink]?.notes?.includes(component.name) || false
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            linkCapabilityToComponent(selectedCapabilityForLink, component.id);
                          }
                        }}
                      />
                      <span className="component-icon">{component.icon}</span>
                      <span className="component-name">{component.name}</span>
                      <span className="component-type">({component.type})</span>
                    </label>
                  </div>
                ))
              ) : (
                <p className="no-components-message">
                  No components found on the canvas. Add components to the architecture first.
                </p>
              )}
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => setShowComponentLink(false)}
                className="modal-button-primary"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerCapabilityAssessment;